import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight, Download, Layers, Network, Search, Zap } from 'lucide-react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useOrgStore } from '../store/useOrgStore';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import { StatCard } from '../components/ui/StatCard';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import { downloadCsv } from '../lib/csv';
import { isManagedObject, isObjectThatMatters, isSystemObject } from '../lib/objectsThatMatter';
import type { DataModelDetail, FlowsResult, ObjectsResult, ObjectSummary } from '../types/org.types';

type Filter = 'all' | 'custom' | 'standard' | 'review' | 'empty';

const FILTER_LABEL: Record<Filter, string> = {
  all: 'All',
  custom: 'Custom',
  standard: 'Standard',
  review: 'Review',
  empty: 'Empty only',
};

const USAGE_LABEL = { 'in-use': 'In use', empty: 'Empty', unknown: 'Unknown' } as const;
const USAGE_TONE = { 'in-use': 'healthy', empty: 'high', unknown: 'medium' } as const;

// Data source: the live audit store by default, which since TYR-216 is the
// client Hub alone. A caller that holds an audit already, such as the stored
// client scan on the internal lead page, passes it in. Passing `data` at all,
// null included, switches the page to scan mode, where NO slice is read from
// the store. A category the scan could not read is stored as null, so null is
// a captured answer ("not available"), never a reason to reach for the live
// org's numbers.
export function ObjectsPage({
  data,
  flowsData,
  detail,
  showIntro = true,
  onOpenAutomation,
}: {
  data?: ObjectsResult | null;
  flowsData?: FlowsResult | null;
  // Stored client scan only (/internal): record types, layouts, OWD and
  // sharing rules per object. Undefined = do not show (Hub); null = the scan
  // did not capture it (pre-v2.6 scan or a failed read).
  detail?: DataModelDetail | null;
  showIntro?: boolean;
  // Given, the automation count opens the by-object view in the same page
  // instead of linking to the /flows route.
  onOpenAutomation?: (apiName: string) => void;
} = {}) {
  const storeObjects = useOrgStore((s) => s.audit.objects);
  const storeFlows = useOrgStore((s) => s.audit.flows);
  const scanMode = data !== undefined;
  const objects = scanMode ? data : storeObjects;
  const flows = scanMode ? flowsData : storeFlows;
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  // Stored client scan only: managed-package objects start hidden.
  const [hideManaged, setHideManaged] = useState(scanMode);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Automation footprint per object — derived client-side from the flows audit.
  // Counts Flows (including legacy Workflow + Process Builders) and the
  // Workflow-Rule rows that share the same trigger object.
  // From v2.4 the per-object inventory is the source, so the number here is the
  // same number the By object tab shows for that object.
  const automationCountByObject = useMemo(() => {
    const map = new Map<string, number>();
    if (!flows) return map;
    if (flows.byObject) {
      for (const g of flows.byObject.groups) {
        if (g.object) map.set(g.object, g.items.length);
      }
      return map;
    }
    for (const f of flows.flows) {
      const key = f.triggerObject;
      if (!key) continue;
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [flows]);

  const totalAutomations = useMemo(
    () => Array.from(automationCountByObject.values()).reduce((a, b) => a + b, 0),
    [automationCountByObject],
  );

  // A scan whose flows audit failed would otherwise show a zero that reads as
  // "this org has no automation". Say not captured instead. A failed capture
  // arrives as null, so test the resolved value, not just for undefined. Live
  // sessions keep the behaviour they had.
  const automationKnown = !scanMode || flows != null;

  // System objects (Share, History, Feed, ChangeEvent, Tag) never show on
  // screen, "Show all" included. By default only the objects that matter show.
  const onScreen = useMemo(
    () => (objects ? objects.objects.filter((o) => !isSystemObject(o.apiName)) : []),
    [objects],
  );
  const matterCount = useMemo(
    () => onScreen.filter((o) => isObjectThatMatters(o, automationCountByObject.get(o.apiName) ?? 0)).length,
    [onScreen, automationCountByObject],
  );
  const hiddenCount = onScreen.length - matterCount;

  const rows = useMemo(() => {
    return onScreen.filter((o) => {
      if (hideManaged && isManagedObject(o.apiName)) return false;
      if (!showAll && !isObjectThatMatters(o, automationCountByObject.get(o.apiName) ?? 0)) return false;
      if (filter === 'custom' && !o.custom) return false;
      if (filter === 'standard' && o.custom) return false;
      if (filter === 'review' && o.status === 'healthy') return false;
      if (filter === 'empty' && o.usage !== 'empty') return false;
      if (search) {
        const q = search.toLowerCase();
        if (!o.label.toLowerCase().includes(q) && !o.apiName.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [onScreen, showAll, hideManaged, automationCountByObject, filter, search]);

  // Schema Hotspots — Most Connected and Most Automated.
  const mostConnected = useMemo(() => {
    return [...onScreen]
      .filter((o) => o.inboundCount + o.outboundCount > 0)
      .sort((a, b) => b.inboundCount + b.outboundCount - (a.inboundCount + a.outboundCount))
      .slice(0, 6);
  }, [onScreen]);

  const mostAutomated = useMemo(() => {
    return [...onScreen]
      .map((o) => ({ ...o, automations: automationCountByObject.get(o.apiName) ?? 0 }))
      .filter((o) => o.automations > 0)
      .sort((a, b) => b.automations - a.automations)
      .slice(0, 6);
  }, [onScreen, automationCountByObject]);

  // Record counts arrived in v2.4. Older stored snapshots carry none, so the
  // record-count blocks stay hidden for them.
  const hasRecordCounts = useMemo(
    () => (objects ? objects.objects.some((o) => o.usage !== undefined) : false),
    [objects],
  );
  const maxRecordCount = useMemo(
    () => (objects ? objects.objects.reduce((m, o) => Math.max(m, o.recordCount ?? 0), 0) : 0),
    [objects],
  );

  if (!objects) {
    return <EmptyState title="No object data. Reconnect to scan again." />;
  }

  const objectTotals = objects.objectTotals;
  const filters: Filter[] = hasRecordCounts
    ? ['all', 'custom', 'standard', 'review', 'empty']
    : ['all', 'custom', 'standard', 'review'];

  const totalFields = objects.objects.reduce((acc, o) => acc + (o.fieldCount ?? 0), 0);
  const totalCustomFields = objects.objects.reduce((acc, o) => acc + o.customFieldCount, 0);

  return (
    <div className="space-y-8">
      {showIntro ? (
        <div>
          <div className="text-xs uppercase tracking-widest text-design mb-1">Design</div>
          <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Object Intelligence</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
            Not just an inventory. It surfaces the schema spine and the automation hotspots so you
            can see which objects are load-bearing, which carry the most logic, and how they connect.
          </p>
        </div>
      ) : null}

      {/* ── Headline stats ──────────────────────────────────────────────── */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        {[
          <StatCard
            key="o"
            label="Objects"
            value={objects.totalCount.toLocaleString()}
            hint={`${objects.objects.filter((o) => o.custom).length} custom in sample`}
            accent="design"
            icon={<Layers size={14} />}
          />,
          <StatCard
            key="f"
            label="Custom fields"
            value={totalCustomFields.toLocaleString()}
            hint={`${totalFields.toLocaleString()} fields described`}
            accent="design"
          />,
          <StatCard
            key="rels"
            label="Schema connections"
            value={objects.objects.reduce((a, o) => a + o.inboundCount + o.outboundCount, 0).toLocaleString()}
            hint={`across ${objects.sampledCount} sampled objects`}
            accent="adopt"
            icon={<Network size={14} />}
          />,
          <StatCard
            key="aut"
            label="Automations"
            value={automationKnown ? totalAutomations.toLocaleString() : 'not captured'}
            hint={
              !automationKnown
                ? undefined
                : flows?.byObject
                  ? 'Flows, process builders, workflow rules, triggers, validation rules'
                  : 'Flows, process builders, workflow rules'
            }
            accent="deliver"
            icon={<Zap size={14} />}
          />,
        ].map((card, i) => (
          <motion.div
            key={i}
            variants={{
              hidden: { opacity: 0, y: 6 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
            }}
          >
            {card}
          </motion.div>
        ))}
      </motion.div>

      {/* ── Data model stats ────────────────────────────────────────────── */}
      {objectTotals ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Custom objects" value={objectTotals.customObjects.toLocaleString()} accent="design" />
          <StatCard label="Hold records" value={objectTotals.holdRecords.toLocaleString()} accent="grow" />
          <StatCard label="Empty" value={objectTotals.empty.toLocaleString()} accent="sunrise" />
          <StatCard
            label="Count not available"
            value={objectTotals.countNotAvailable.toLocaleString()}
          />
        </div>
      ) : null}

      {/* ── Schema Hotspots ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HotspotsCard
          title="Most Connected Objects"
          subtitle="Relationship hubs: these are the schema spine"
          accent="adopt"
          icon={<Network size={14} />}
          rows={mostConnected.map((o) => ({
            apiName: o.apiName,
            label: o.label,
            primary: o.inboundCount + o.outboundCount,
            secondary: `${o.inboundCount} in · ${o.outboundCount} out`,
          }))}
          emptyHint="No relationship data sampled."
        />
        <HotspotsCard
          title="Most Automated Objects"
          subtitle="Automation hotspots: change-management risk"
          accent="deliver"
          icon={<Zap size={14} />}
          rows={mostAutomated.map((o) => ({
            apiName: o.apiName,
            label: o.label,
            primary: o.automations,
            secondary: `${o.automations} automation${o.automations === 1 ? '' : 's'}`,
          }))}
          emptyHint={
            automationKnown
              ? 'No automation attached to sampled objects.'
              : 'The automation audit was not captured for this scan.'
          }
        />
      </div>

      {/* ── Filters + table ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search objects…"
            className="bg-surface-2 border border-border rounded-md pl-9 pr-3 py-2 text-sm text-crux-light placeholder:text-neutral-600 focus:outline-none focus:border-design w-64"
          />
        </div>
        <div className="flex gap-1">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                filter === f
                  ? 'bg-design/15 border-design/40 text-design'
                  : 'bg-surface-2 border-border text-neutral-400 hover:text-crux-light'
              }`}
            >
              {FILTER_LABEL[f]}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show all
          {!showAll && hiddenCount > 0 ? (
            <span className="text-neutral-500">({hiddenCount.toLocaleString()} hidden: standard objects with no records or automation)</span>
          ) : null}
        </label>
        {scanMode ? (
          <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
            <input type="checkbox" checked={hideManaged} onChange={(e) => setHideManaged(e.target.checked)} />
            Hide managed
          </label>
        ) : null}
        <span className="ml-auto text-xs text-neutral-500">{rows.length} shown</span>
        <button
          type="button"
          onClick={() => downloadObjectsCsv(rows, automationCountByObject, automationKnown)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-border bg-surface-2 text-neutral-400 hover:text-crux-light"
        >
          <Download size={13} />
          Download CSV
        </button>
      </div>

      <ObjectsTable
        rows={rows}
        automationByObject={automationCountByObject}
        automationKnown={automationKnown}
        showRecordCounts={hasRecordCounts}
        maxRecordCount={maxRecordCount}
        expanded={expanded}
        onToggle={(name) => setExpanded(expanded === name ? null : name)}
        onOpenAutomation={onOpenAutomation}
        detail={detail}
      />
      {hasRecordCounts ? (
        <div className="text-xs text-neutral-500">
          Record counts are approximate (Salesforce record count endpoint).
        </div>
      ) : null}

      {/* ── Field type distribution (org-wide) ──────────────────────────── */}
      <CollapsibleSection
        title="Field type distribution (org-wide)"
        meta={`${objects.fieldTypeOrgWide.length} types across ${objects.sampledCount} sampled objects`}
        defaultOpen={false}
      >
        {objects.fieldTypeOrgWide.length === 0 ? (
          <div className="text-sm text-neutral-500 py-6">No field type data sampled.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, objects.fieldTypeOrgWide.length * 22)}>
            <BarChart
              data={objects.fieldTypeOrgWide}
              layout="vertical"
              margin={{ left: 60, right: 24, top: 4, bottom: 4 }}
            >
              <XAxis type="number" tick={{ fill: '#6b6b66', fontSize: 10 }} stroke="#DAD8D1" />
              <YAxis
                type="category"
                dataKey="type"
                tick={{ fill: '#bbb', fontSize: 11 }}
                stroke="#DAD8D1"
                width={120}
              />
              <Tooltip
                contentStyle={{ background: '#E7E5DF', border: '1px solid #DAD8D1', borderRadius: 0 }}
                itemStyle={{ color: '#1B1B1B' }}
              />
              <Bar dataKey="count" fill="#3D55D6" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CollapsibleSection>
    </div>
  );
}

// ─── CSV export (one row per object currently shown) ────────────────────────
const OBJECT_CSV_HEADERS = [
  'Label',
  'API name',
  'Custom',
  'Records',
  'Usage',
  'Fields',
  'Custom fields',
  'Relationships',
  'Automation items',
];

function downloadObjectsCsv(
  rows: ObjectSummary[],
  automationByObject: Map<string, number>,
  automationKnown: boolean,
) {
  downloadCsv(
    'orgiq-objects.csv',
    OBJECT_CSV_HEADERS,
    rows.map((o) => [
      o.label,
      o.apiName,
      o.custom ? 'yes' : 'no',
      o.recordCount ?? '',
      o.usage ? USAGE_LABEL[o.usage] : '',
      o.fieldCount ?? '',
      o.customFieldCount,
      o.inboundCount + o.outboundCount,
      automationKnown ? (automationByObject.get(o.apiName) ?? 0) : '',
    ]),
  );
}

// ─── Hotspots card ──────────────────────────────────────────────────────────
function HotspotsCard({
  title,
  subtitle,
  accent,
  icon,
  rows,
  emptyHint,
}: {
  title: string;
  subtitle: string;
  accent: 'adopt' | 'deliver' | 'design' | 'grow';
  icon: React.ReactNode;
  rows: { apiName: string; label: string; primary: number; secondary: string }[];
  emptyHint: string;
}) {
  const accentText =
    accent === 'adopt'
      ? 'text-adopt'
      : accent === 'deliver'
        ? 'text-deliver'
        : accent === 'grow'
          ? 'text-grow'
          : 'text-design';
  const accentBar =
    accent === 'adopt'
      ? 'bg-adopt'
      : accent === 'deliver'
        ? 'bg-deliver'
        : accent === 'grow'
          ? 'bg-grow'
          : 'bg-design';
  const max = rows.reduce((m, r) => Math.max(m, r.primary), 0);
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-5">
      <div className="flex items-center gap-2 mb-1">
        <span className={accentText}>{icon}</span>
        <span className="text-[11px] uppercase tracking-widest text-neutral-500">{title}</span>
      </div>
      <p className="text-xs text-neutral-500 mb-4">{subtitle}</p>
      {rows.length === 0 ? (
        <div className="text-sm text-neutral-500 py-3">{emptyHint}</div>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((r) => {
            const pct = max > 0 ? (r.primary / max) * 100 : 0;
            return (
              <li key={r.apiName} className="text-sm">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <span className="text-crux-light truncate flex-1">{r.label}</span>
                  <span className={`tabular-nums font-semibold ${accentText}`}>{r.primary}</span>
                </div>
                <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${accentBar}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5 flex items-center justify-between">
                  <span className="font-mono truncate">{r.apiName}</span>
                  <span className="shrink-0 ml-2">{r.secondary}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Table with per-row expand ──────────────────────────────────────────────
function ObjectsTable({
  rows,
  automationByObject,
  automationKnown,
  showRecordCounts,
  maxRecordCount,
  expanded,
  onToggle,
  onOpenAutomation,
  detail,
}: {
  rows: ObjectSummary[];
  automationByObject: Map<string, number>;
  automationKnown: boolean;
  showRecordCounts: boolean;
  maxRecordCount: number;
  expanded: string | null;
  onToggle: (name: string) => void;
  onOpenAutomation?: (apiName: string) => void;
  detail?: DataModelDetail | null;
}) {
  if (rows.length === 0) {
    return <div className="text-sm text-neutral-500 py-6">No objects matched.</div>;
  }
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-2">
          <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
            <th className="w-6"></th>
            <th className="text-left px-4 py-3 font-medium">Object</th>
            <th className="text-left px-4 py-3 font-medium">Type</th>
            {showRecordCounts ? (
              <>
                <th className="text-right px-4 py-3 font-medium">Records</th>
                <th className="px-2 py-3 w-32"></th>
                <th className="text-left px-4 py-3 font-medium">Usage</th>
              </>
            ) : null}
            <th className="text-right px-4 py-3 font-medium">Fields</th>
            <th className="text-right px-4 py-3 font-medium">Custom</th>
            <th className="text-right px-4 py-3 font-medium">Connections</th>
            <th className="text-right px-4 py-3 font-medium">Automations</th>
            <th className="text-left px-4 py-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o, i) => {
            const automations = automationByObject.get(o.apiName) ?? 0;
            const isOpen = expanded === o.apiName;
            return (
              <ObjectRow
                key={o.apiName}
                object={o}
                automations={automations}
                automationKnown={automationKnown}
                showRecordCounts={showRecordCounts}
                maxRecordCount={maxRecordCount}
                isOpen={isOpen}
                rowIndex={i}
                onToggle={() => onToggle(o.apiName)}
                onOpenAutomation={onOpenAutomation}
                detail={detail}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ObjectRow({
  object,
  automations,
  automationKnown,
  showRecordCounts,
  maxRecordCount,
  isOpen,
  rowIndex,
  onToggle,
  onOpenAutomation,
  detail,
}: {
  object: ObjectSummary;
  automations: number;
  automationKnown: boolean;
  showRecordCounts: boolean;
  maxRecordCount: number;
  isOpen: boolean;
  rowIndex: number;
  onToggle: () => void;
  onOpenAutomation?: (apiName: string) => void;
  detail?: DataModelDetail | null;
}) {
  const showDetail = detail !== undefined;
  const rowBg = rowIndex % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent';
  const STATUS_TONE = { healthy: 'healthy', 'low-usage': 'medium', review: 'high' } as const;
  const STATUS_LABEL = { healthy: 'Healthy', 'low-usage': 'Low usage', review: 'Review' } as const;
  const canExpand =
    showDetail ||
    object.inboundCount + object.outboundCount + automations + object.fieldTypeBreakdown.length > 0;

  return (
    <>
      <tr
        className={`${rowBg} ${canExpand ? 'cursor-pointer hover:bg-surface-2/40' : ''}`}
        onClick={canExpand ? onToggle : undefined}
      >
        <td className="w-6 pl-3 text-neutral-500">
          {canExpand ? (
            isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          ) : null}
        </td>
        <td className="px-4 py-2.5">
          <div className="text-crux-light">{object.label}</div>
          <div className="text-[11px] text-neutral-600 font-mono">{object.apiName}</div>
        </td>
        <td className="px-4 py-2.5 text-neutral-400">{object.custom ? 'Custom' : 'Standard'}</td>
        {showRecordCounts ? (
          <>
            <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
              {object.recordCount === null || object.recordCount === undefined
                ? <span className="text-neutral-600">not available</span>
                : object.recordCount.toLocaleString()}
            </td>
            <td className="px-2 py-2.5">
              {object.recordCount && maxRecordCount > 0 ? (
                <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-design"
                    style={{ width: `${(object.recordCount / maxRecordCount) * 100}%`, minWidth: 2 }}
                  />
                </div>
              ) : null}
            </td>
            <td className="px-4 py-2.5">
              {object.usage ? (
                <RiskBadge tone={USAGE_TONE[object.usage]}>{USAGE_LABEL[object.usage]}</RiskBadge>
              ) : null}
            </td>
          </>
        ) : null}
        <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
          {object.fieldCount ?? '-'}
        </td>
        <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
          {object.customFieldCount}
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums">
          {object.inboundCount + object.outboundCount > 0 ? (
            <span className="text-adopt">
              {object.inboundCount + object.outboundCount}
              <span className="text-neutral-600 text-xs ml-1">
                ({object.inboundCount}/{object.outboundCount})
              </span>
            </span>
          ) : (
            <span className="text-neutral-600">-</span>
          )}
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums">
          {!automationKnown ? (
            <span className="text-neutral-600">n/a</span>
          ) : automations > 0 ? (
            onOpenAutomation ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAutomation(object.apiName);
                }}
                className="text-deliver hover:underline tabular-nums"
              >
                {automations}
              </button>
            ) : (
              <Link
                to={`../flows?tab=by-object&object=${encodeURIComponent(object.apiName)}`}
                relative="path"
                onClick={(e) => e.stopPropagation()}
                className="text-deliver hover:underline"
              >
                {automations}
              </Link>
            )
          ) : (
            <span className="text-neutral-600">0</span>
          )}
        </td>
        <td className="px-4 py-2.5">
          <RiskBadge tone={STATUS_TONE[object.status]}>{STATUS_LABEL[object.status]}</RiskBadge>
        </td>
      </tr>
      {isOpen && canExpand ? (
        <tr className={rowBg}>
          <td colSpan={showRecordCounts ? 11 : 8} className="px-4 pb-4 pt-1 pl-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
              <RelationshipColumn
                title="Inbound: referenced BY"
                hint="Objects whose lookups point at this object"
                items={object.inboundRelationships}
              />
              <RelationshipColumn
                title="Outbound: references TO"
                hint="Fields on this object that look up other objects"
                items={object.outboundRelationships}
              />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
                  Field type mix
                </div>
                {object.fieldTypeBreakdown.length === 0 ? (
                  <div className="text-xs text-neutral-600">Not described (outside top 50).</div>
                ) : (
                  <ul className="space-y-1.5">
                    {object.fieldTypeBreakdown.slice(0, 8).map((b) => (
                      <li key={b.type} className="flex items-center justify-between text-xs">
                        <span className="text-crux-light">{b.type}</span>
                        <span className="text-neutral-400 tabular-nums">{b.count}</span>
                      </li>
                    ))}
                    {object.fieldTypeBreakdown.length > 8 ? (
                      <li className="text-[10px] text-neutral-600">
                        + {object.fieldTypeBreakdown.length - 8} more
                      </li>
                    ) : null}
                  </ul>
                )}
              </div>
            </div>
            {showDetail ? <ObjectDetailBlock apiName={object.apiName} detail={detail ?? null} /> : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

// Record types, layouts, OWD and sharing rule count for one object, from the
// stored scan. Each slice the scan did not read says so; nothing is inferred.
function ObjectDetailBlock({ apiName, detail }: { apiName: string; detail: DataModelDetail | null }) {
  const notCaptured = <span className="text-neutral-600">Not captured in this scan</span>;
  const recordTypes = detail?.recordTypes?.filter((r) => r.object === apiName);
  const layouts = detail?.layouts?.filter((l) => l.object === apiName);
  const owd = detail?.sharingModels?.find((m) => m.object === apiName);
  const rules = detail?.sharingRuleCounts;
  const cell = 'text-[11px] uppercase tracking-widest text-neutral-500 mb-1';
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-5 text-xs mt-4 pt-3 border-t border-border">
      <div>
        <div className={cell}>Record types</div>
        {!recordTypes ? notCaptured : recordTypes.length === 0 ? (
          <span className="text-neutral-600">None</span>
        ) : (
          <ul className="space-y-1">
            {recordTypes.map((r) => (
              <li key={r.developerName} className="text-crux-light">
                {r.name}
                {r.active ? null : <span className="text-neutral-600 ml-1">(inactive)</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className={cell}>Page layouts{layouts ? ` (${layouts.length})` : ''}</div>
        {!layouts ? notCaptured : layouts.length === 0 ? (
          <span className="text-neutral-600">None</span>
        ) : (
          <ul className="space-y-1">
            {layouts.map((l, i) => (
              <li key={`${l.name}-${i}`} className="text-crux-light">{l.name}</li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className={cell}>Sharing model (OWD)</div>
        {!detail?.sharingModels ? notCaptured : !owd ? (
          <span className="text-neutral-600">Not listed</span>
        ) : (
          <div className="text-crux-light">
            Internal: {owd.internal ?? 'n/a'}
            <br />
            External: {owd.external ?? 'n/a'}
          </div>
        )}
      </div>
      <div>
        <div className={cell}>Sharing rules</div>
        {!rules ? notCaptured : <span className="text-crux-light tabular-nums">{rules[apiName] ?? 0}</span>}
      </div>
    </div>
  );
}

function RelationshipColumn({
  title,
  hint,
  items,
}: {
  title: string;
  hint: string;
  items: { object: string; label: string | null; throughField: string }[];
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500">{title}</div>
      <div className="text-xs text-neutral-600 mb-2">{hint}</div>
      {items.length === 0 ? (
        <div className="text-xs text-neutral-600">None.</div>
      ) : (
        <ul className="space-y-1">
          {items.map((r, i) => (
            <li
              key={`${r.object}-${r.throughField}-${i}`}
              className="flex items-baseline justify-between gap-2 text-xs"
            >
              <span className="text-crux-light truncate">{r.label ?? r.object}</span>
              <span className="text-[10px] text-neutral-600 font-mono truncate text-right shrink-0 max-w-[55%]">
                {r.throughField}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
